'use client';
import React from 'react';
import { createPortal } from 'react-dom';
import { I, card, ConfirmButton, BoutonIcone, COUCHES, useConfirmation } from '@/components/ui';
import { Bouton } from '@/shared/Bouton';
import { styleBouton } from '@/shared/boutonStyles';
import type { RhIntegrationPiece } from '@/lib/types';

/**
 * LES PIÈCES DU DOSSIER D'UNE PERSONNE — une seule définition, deux appelants.
 *
 * ── Pourquoi ce fichier existe (RQ, 06/10/2026) ────────────────────────────
 *
 * « Je voudrais que le document soit ici pour modifier ou rajouter des
 * documents si besoin (bien que l'intégration soit terminée). »
 *
 * Le bloc vivait dans `RhIntegration.tsx` et n'était atteignable que depuis le
 * parcours d'intégration. Le dossier RH étant devenu le hub de la personne, il
 * lui faut les pièces — en écriture, pas en vitrine. La façon rapide aurait été
 * de recopier le composant : c'est exactement le schéma des deux « jumeaux »
 * des 12 et 13/09/2026 — deux copies, un avertissement ajouté d'un seul côté, et
 * la seconde fenêtre qui finit par ne plus dire que la consultation est tracée.
 * D'où l'extraction, sur le modèle de `RattacherCompte`.
 *
 * ── ⚠️ UN DOSSIER CLÔTURÉ N'EST PLUS FIGÉ POUR SES PIÈCES ─────────────────
 *
 * Le bloc refusait dépôt et retrait dès que le parcours était clos (`clos`).
 * Trois raisons de lever ce verrou, et pas seulement la demande :
 *
 *  · le moteur ne l'a JAMAIS exigé — `POST /ged/rh/personnel/:orgId/pieces` ne
 *    regarde pas `statut` ; le verrou n'était donc qu'à l'écran ;
 *  · la rubrique s'intitule, dans le parcours lui-même, « AU FIL DE L'EAU » :
 *    des pièces qui arrivent avec le temps, par définition ;
 *  · une pièce administrative s'ajoute toute la vie — un diplôme obtenu en
 *    2027, un RIB qui change de banque, une CIN renouvelée. Les refuser après
 *    la clôture obligeait à rouvrir un parcours terminé pour déposer un
 *    papier : un dossier d'intégration « en cours » qui ne l'est pas, c'est-à-
 *    dire une affirmation fausse au seul motif d'un classement.
 *
 * `clos` subsiste et ne sert plus qu'à le DIRE — « le parcours est clôturé, les
 * pièces continuent de s'y classer » —, jamais à bloquer. Les deux appelants
 * ont donc le même pouvoir : un seul composant, une seule règle, aucune
 * divergence à tenir.
 *
 * ── Ce qui ne change pas ───────────────────────────────────────────────────
 *
 * ⚠️ LE CONTENU NE SORT QUE PIÈCE PAR PIÈCE, et chaque passage laisse une trace
 * nominative au journal (`IntegrationPieceConsultee`). La liste ne porte que des
 * métadonnées : ni `GET /ged/rh/personnel/:orgId/pieces` ni la liste des parcours
 * ne servent `fichierData`. Une pièce
 * marquée « donnée personnelle » — identité, photo, coordonnées bancaires — pose
 * en plus une confirmation qui annonce la trace AVANT de l'écrire.
 *
 * ⚠️ DÉPÔT, LECTURE ET RETRAIT EXIGENT LE RÔLE `rh` côté moteur (`exigerRh`,
 * 05/10/2026) — et non « RQ ou RH » comme le reste de la fiche. C'est à
 * l'appelant de passer `modifiable` en conséquence : proposer les boutons à un
 * RQ sans ce rôle donnerait un 403 à chaque clic, ce qui se lit comme une panne
 * et non comme un droit manquant.
 */

/** La check-list `RH LIS check list recrutement personnel`, dans son ordre. */
export const PIECES_ATTENDUES: { cle: string; libelle: string; sensible: boolean }[] = [
  { cle: 'cv', libelle: 'CV', sensible: false },
  { cle: 'lettre', libelle: 'Lettre de motivation', sensible: false },
  { cle: 'formations', libelle: 'Formations', sensible: false },
  { cle: 'diplomes', libelle: 'Diplômes', sensible: false },
  { cle: 'cin', libelle: 'Copie carte d’identité', sensible: true },
  { cle: 'photo', libelle: 'Photo', sensible: true },
  { cle: 'rib', libelle: 'Copie RIB bancaire', sensible: true },
  { cle: 'engagement_qualite', libelle: 'Engagement qualité signé', sensible: false },
  { cle: 'engagement_confidentialite', libelle: 'Engagement confidentialité signé', sensible: false },
];

/** Une pièce lue, prête à être montrée : un blob local, jamais une URL serveur. */
type Lue = { url: string; nom: string; type: string };

/** Le type MIME vient de l'extension du fichier déposé (PDF ou image seulement). */
const typeDe = (nom: string): string => {
  const ext = (nom.split('.').pop() || '').toLowerCase();
  if (ext === 'pdf') return 'application/pdf';
  if (ext === 'jpg' || ext === 'jpeg') return 'image/jpeg';
  if (ext === 'png') return 'image/png';
  if (ext === 'heic') return 'image/heic';
  if (ext === 'heif') return 'image/heif';
  return 'application/octet-stream';
};

const LARGEUR_APERCU = 360;
const HAUTEUR_APERCU = 460;

export function BlocPieces({ pieces, clos = false, modifiable = true, onDeposer, onConsulter, onSupprimer }: {
  /** ⚠️ LES PIÈCES, ET NON UN DOSSIER D'INTÉGRATION (08/10/2026). Le composant
   *  recevait `dossier: RhIntegration` et n'en lisait que `.pieces` : il était
   *  donc impossible de l'employer pour quelqu'un sans parcours, c'est-à-dire
   *  pour tout le personnel entré avant 2026. Il reçoit maintenant la liste, et
   *  l'appelant la tient d'où il veut — du parcours, ou de la personne. */
  pieces: RhIntegrationPiece[];
  /** Le parcours est clôturé. INFORMATIF : il ne bloque plus rien — voir
   *  l'en-tête de ce fichier pour les trois raisons. */
  clos?: boolean;
  /** Le rôle `rh` est porté. Faux : la liste se lit, rien ne s'écrit et aucune
   *  pièce ne s'ouvre — le moteur refuserait les trois. */
  modifiable?: boolean;
  onDeposer: (cle: string, libelle: string, f: File) => void;
  onConsulter: (id: string) => Promise<{ fichierNom: string; fichierData: string } | null>;
  onSupprimer: (id: string) => void;
}) {
  const { demander, confirmation } = useConfirmation();
  const parCle = new Map<string, RhIntegrationPiece>();
  for (const p of pieces) parCle.set(p.cle, p);
  /* Les pièces déposées sous une clé HORS check-list — il n'y en a pas
     aujourd'hui (les deux écrans n'offrent que les neuf), mais une reprise de
     données ou une clé retirée de la liste en produirait. Les taire ferait
     disparaître un document du dossier sans que rien ne le dise. */
  const horsListe = pieces.filter((p) => !PIECES_ATTENDUES.some((a) => a.cle === p.cle));

  /** Confirmation d'une donnée personnelle, puis lecture du contenu — c'est
   *  cette lecture qui laisse la trace nominative au journal. */
  const lire = async (p: RhIntegrationPiece, verbe: string) => {
    if (p.sensible && !(await demander(
      `« ${p.libelle} » est une donnée personnelle.\n\nVotre consultation sera enregistrée à votre nom dans le journal du laboratoire.\n\n${verbe} ?`,
      { confirmer: verbe },
    ))) return null;
    const d = await onConsulter(p.id);
    return d?.fichierData ? d : null;
  };
  const enOctets = (b64: string) => {
    const bin = atob(b64);
    const buf = new Uint8Array(bin.length);
    for (let i = 0; i < bin.length; i += 1) buf[i] = bin.charCodeAt(i);
    return buf;
  };

  const telecharger = async (p: RhIntegrationPiece) => {
    const d = await lire(p, 'Ouvrir');
    if (!d) return;
    const url = URL.createObjectURL(new Blob([enOctets(d.fichierData)]));
    const a = document.createElement('a');
    a.href = url; a.download = d.fichierNom || p.libelle;
    a.click();
    URL.revokeObjectURL(url);
  };

  /* ── L'APERÇU AU SURVOL DE L'ŒIL (RQ, 22/10/2026) ─────────────────────────
   *
   * ⚠️ LIRE UNE PIÈCE LAISSE UNE TRACE NOMINATIVE — le survol n'y échappe pas.
   * Deux garde-fous, donc :
   *  · un délai (400 ms) : une souris qui traverse la colonne ne consulte rien ;
   *  · une pièce « donnée personnelle » ne se charge JAMAIS au survol : la
   *    bulle dit qu'il faut cliquer, et c'est le clic qui pose la confirmation
   *    annonçant la trace AVANT de l'écrire. Son contenu n'est pas gardé une
   *    fois la souris partie : le survol suivant redemande le clic.
   * Les autres sont lues une fois et gardées pour la durée du bloc — repasser
   * dessus ne relit pas, donc ne retrace pas. */
  const [survol, setSurvol] = React.useState<{ id: string; x: number; y: number; gauche: boolean } | null>(null);
  const [lues, setLues] = React.useState<Record<string, Lue | 'echec'>>({});
  const courant = React.useRef<string | null>(null);
  const minuteur = React.useRef<number | null>(null);
  const luesRef = React.useRef(lues);
  luesRef.current = lues;
  React.useEffect(() => () => {
    if (minuteur.current !== null) window.clearTimeout(minuteur.current);
    for (const v of Object.values(luesRef.current)) if (v !== 'echec') URL.revokeObjectURL(v.url);
  }, []);

  type Pos = { id: string; x: number; y: number; gauche: boolean };
  const charger = async (p: RhIntegrationPiece, avecConfirmation: boolean, pos: Pos) => {
    if (luesRef.current[p.id]) return;
    const d = avecConfirmation ? await lire(p, 'Afficher') : await onConsulter(p.id);
    if (avecConfirmation && !d) return;
    if (!d?.fichierData) { setLues((m) => ({ ...m, [p.id]: 'echec' })); return; }
    const type = typeDe(d.fichierNom);
    const url = URL.createObjectURL(new Blob([enOctets(d.fichierData)], { type }));
    setLues((m) => ({ ...m, [p.id]: { url, nom: d.fichierNom, type } }));
    /* La fenêtre de confirmation a pris le focus, ce qui a fermé la bulle :
       on la rouvre, la lecture étant faite. */
    if (avecConfirmation) { courant.current = p.id; setSurvol(pos); }
  };
  const sensiblesRef = React.useRef(new Set<string>());
  sensiblesRef.current = new Set(pieces.filter((q) => q.sensible).map((q) => q.id));
  /** Referme la bulle et oublie le contenu des pièces sensibles. */
  const fermer = React.useCallback(() => {
    if (minuteur.current !== null) { window.clearTimeout(minuteur.current); minuteur.current = null; }
    courant.current = null;
    setSurvol(null);
    setLues((m) => {
      let change = false;
      const reste: Record<string, Lue | 'echec'> = {};
      for (const [id, v] of Object.entries(m)) {
        if (sensiblesRef.current.has(id) && v !== 'echec') { URL.revokeObjectURL(v.url); change = true; } else reste[id] = v;
      }
      return change ? reste : m;
    });
  }, []);

  /* Un clic ailleurs ou Échap referme : après la confirmation, la souris n'est
     plus sur l'œil et aucun « mouseleave » ne viendrait fermer la bulle. */
  React.useEffect(() => {
    if (!survol) return undefined;
    const ferme = () => fermer();
    const touche = (e: KeyboardEvent) => { if (e.key === 'Escape') fermer(); };
    document.addEventListener('mousedown', ferme);
    document.addEventListener('keydown', touche);
    return () => { document.removeEventListener('mousedown', ferme); document.removeEventListener('keydown', touche); };
  }, [survol, fermer]);

  const positionDe = (p: RhIntegrationPiece, el: HTMLElement): Pos => {
    const r = el.getBoundingClientRect();
    const gauche = r.left - LARGEUR_APERCU - 12 >= 8;
    const x = gauche ? r.left - LARGEUR_APERCU - 8 : r.right + 8;
    const haut = Math.min(Math.max(8, r.top - HAUTEUR_APERCU / 2), window.innerHeight - HAUTEUR_APERCU - 8);
    return { id: p.id, x, y: Math.max(8, haut), gauche };
  };
  const survoler = (p: RhIntegrationPiece, e: React.MouseEvent | React.FocusEvent) => {
    const pos = positionDe(p, e.currentTarget as HTMLElement);
    courant.current = p.id;
    if (minuteur.current !== null) window.clearTimeout(minuteur.current);
    minuteur.current = window.setTimeout(() => {
      if (courant.current !== p.id) return;
      setSurvol(pos);
      if (!p.sensible) void charger(p, false, pos);
    }, 400);
  };
  const cliquerOeil = (p: RhIntegrationPiece, e: React.MouseEvent) => {
    const pos = positionDe(p, e.currentTarget as HTMLElement);
    courant.current = p.id;
    setSurvol(pos);
    void charger(p, p.sensible, pos);
  };

  const pieceSurvolee = survol ? pieces.find((q) => q.id === survol.id) : undefined;
  const apercu = survol && pieceSurvolee && typeof document !== 'undefined' ? createPortal(
    <div aria-hidden style={{
      position: 'fixed', left: survol.x, top: survol.y, width: LARGEUR_APERCU, height: HAUTEUR_APERCU,
      zIndex: COUCHES.TRAVAIL - 1, pointerEvents: 'none', display: 'flex', flexDirection: 'column',
      background: 'var(--surface-card, #fff)', border: '1px solid var(--border-default)',
      borderRadius: 'var(--radius-md)', boxShadow: 'var(--shadow-md)', overflow: 'hidden',
    }}>
      {(() => {
        const v = lues[pieceSurvolee.id];
        const msg = (t: string) => (
          <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20,
            textAlign: 'center', fontSize: 12.5, color: 'var(--text-muted)', lineHeight: 1.55 }}>{t}</div>
        );
        if (!v) {
          return pieceSurvolee.sensible
            ? msg('Donnée personnelle : cliquez sur l’œil pour l’afficher. La consultation sera enregistrée à votre nom dans le journal.')
            : msg('Chargement de l’aperçu…');
        }
        if (v === 'echec') return msg('Aperçu indisponible.');
        if (v.type === 'application/pdf') {
          return <iframe src={`${v.url}#toolbar=0&navpanes=0`} title={`Aperçu de ${pieceSurvolee.libelle}`}
            style={{ flex: 1, border: 0, width: '100%' }} />;
        }
        if (v.type === 'image/jpeg' || v.type === 'image/png') {
          // eslint-disable-next-line @next/next/no-img-element
          return <img src={v.url} alt={`Aperçu de ${pieceSurvolee.libelle}`}
            style={{ flex: 1, minHeight: 0, width: '100%', objectFit: 'contain', background: 'var(--surface-sunken)' }} />;
        }
        return msg('Aperçu indisponible pour ce format (HEIC/HEIF) : téléchargez la pièce pour la voir.');
      })()}
    </div>,
    document.body,
  ) : null;

  /** Un dépôt de fichier, présenté comme un bouton-icône. Le `label` enveloppe le
   *  champ fichier : le clic ouvre le sélecteur, sans bouton intermédiaire. */
  const depot = (cle: string, libelle: string, icone: string, action: string) => (
    <label title={action} aria-label={action} className="ged-btn"
      style={{ ...styleBouton('contour', 'sm'), cursor: 'pointer', padding: 0, width: 30, minWidth: 30, justifyContent: 'center' }}>
      <I n={icone} s={14} />
      <input type="file" accept=".pdf,.jpg,.jpeg,.png,.heic,.heif" style={{ display: 'none' }}
        title="PDF ou image, 8 Mo maximum"
        onChange={(e) => { const f = e.target.files?.[0]; if (f) onDeposer(cle, libelle, f); e.target.value = ''; }} />
    </label>
  );

  /** Une ligne de la check-list, ou une pièce hors liste. */
  const ligne = (cle: string, libelle: string, sensible: boolean, p?: RhIntegrationPiece) => (
    <div key={cle} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '9px 0',
      borderTop: '1px solid var(--border-subtle)' }}>
      {/* Pièce absente : pas de pictogramme plutôt qu'une croix — elle n'est
          pas refusée, elle n'est pas encore là. Le bouton « Déposer » dit quoi faire. */}
      <span style={{ flex: 'none', width: 18, color: '#1F7A45' }}>
        {p ? <I n="check" s={13} /> : null}
      </span>
      <div style={{ flex: 1, minWidth: 0 }}>
        <span style={{ fontSize: 12.5, color: p ? 'var(--text-strong)' : 'var(--text-muted)' }}>
          {libelle}
          {/* Le cadenas remplace la pastille écrite « donnée personnelle » : même
              information, dite à l'infobulle et au lecteur d'écran. */}
          {sensible && (
            <span role="img" aria-label="Donnée personnelle" title="Donnée personnelle — chaque consultation est enregistrée à votre nom"
              style={{ display: 'inline-block', marginLeft: 7, verticalAlign: '-2px', color: 'var(--amber-600)' }}>
              <I n="lock" s={13} />
            </span>
          )}
        </span>
        {p && (
          <span style={{ display: 'block', fontSize: 12.5, color: 'var(--text-muted)' }}>
            {p.fichierNom} · déposée le {p.deposeLe} par {p.deposePar}
          </span>
        )}
      </div>
      {p ? (
        modifiable && (
          <>
            {/* L'ŒIL : l'aperçu s'affiche au survol (ou au clic, pour une donnée
                personnelle — voir plus haut). */}
            <Bouton taille="sm" variante="contour" title={`Aperçu de ${libelle}`} aria-label={`Aperçu de ${libelle}`}
              onMouseEnter={(e) => survoler(p, e)} onMouseLeave={fermer}
              onFocus={(e) => survoler(p, e)} onBlur={fermer}
              onClick={(e) => cliquerOeil(p, e)}
              style={{ padding: 0, width: 30, minWidth: 30, justifyContent: 'center' }}>
              <I n="eye" s={14} />
            </Bouton>
            <BoutonIcone icone="download" action={`Télécharger ${libelle}`} onClick={() => void telecharger(p)} />
            {/* REMPLACER et non « retirer puis déposer » : une CIN renouvelée,
                un RIB qui change de banque. Le moteur crée une nouvelle ligne
                et la liste retient la dernière par clé — l'ancienne n'est donc
                pas perdue au journal, mais elle sort du dossier courant. */}
            {depot(cle, libelle, 'refresh', `Remplacer ${libelle}`)}
            <ConfirmButton label="" icon="trash" titre={`Retirer ${libelle} du dossier`} variant="danger"
              question={`Retirer « ${libelle} » du dossier ?`} onConfirm={() => onSupprimer(p.id)} />
          </>
        )
      ) : modifiable && depot(cle, libelle, 'upload', `Déposer ${libelle}`)}
    </div>
  );

  return (
    <div style={{ ...card, padding: 18, marginTop: 12 }}>
      <h3 style={{ margin: 0, fontSize: 13, fontWeight: 700, textTransform: 'uppercase',
        letterSpacing: '0.05em', color: 'var(--text-strong)' }}>
        Pièces du dossier
      </h3>
      <p style={{ fontSize: 12.5, color: 'var(--text-muted)', margin: '6px 0 12px', lineHeight: 1.55 }}>
        Check-list de recrutement du laboratoire. Les pièces marquées
        <strong> donnée personnelle </strong>— identité, photo, coordonnées bancaires — ne sortent
        jamais dans une liste : elles s’ouvrent une par une, et <strong>chaque consultation est
        enregistrée à votre nom</strong> dans le journal.
      </p>
      {/* Le fait, sans la conséquence qu'il n'a plus : la clôture ne ferme pas
          le classement. Le taire laisserait croire qu'on modifie un dossier
          encore en cours. */}
      {clos && (
        <p style={{ fontSize: 12.5, color: 'var(--text-muted)', margin: '-6px 0 12px', lineHeight: 1.55 }}>
          Le parcours d’intégration est <strong>clôturé</strong> ; les pièces continuent de s’y
          classer — un diplôme obtenu plus tard, une carte d’identité renouvelée.
        </p>
      )}
      {!modifiable && (
        <p style={{ fontSize: 12.5, color: 'var(--text-faint)', margin: '-6px 0 12px', lineHeight: 1.55 }}>
          Déposer, ouvrir ou retirer une pièce relève du <strong>gestionnaire RH</strong> : ce sont
          des pièces administratives, et leur contenu ne sort que sur une trace nominative.
        </p>
      )}

      {PIECES_ATTENDUES.map((att) => ligne(att.cle, att.libelle, att.sensible, parCle.get(att.cle)))}
      {!!horsListe.length && (
        <>
          <p style={{ fontSize: 12, color: 'var(--text-muted)', margin: '12px 0 0', lineHeight: 1.5 }}>
            Hors check-list — déposées sous une autre clé :
          </p>
          {horsListe.map((p) => ligne(p.cle, p.libelle || p.cle, p.sensible, p))}
        </>
      )}
      {confirmation}
      {apercu}
    </div>
  );
}
